"use client";

import { useRef } from "react";
import dynamic from "next/dynamic";
import { motion, useReducedMotion, useScroll, useTransform, type MotionValue } from "framer-motion";
import { SectionHeading } from "@/components/ui/section-heading";
import { Reveal } from "@/components/ui/reveal";

const DieCanvas = dynamic(() => import("@/components/three/die-canvas"), { ssr: false });

/**
 * NPU-native inference in Pakistan.
 *
 * Replaced "The cost problem" on 2026-08-25. That section argued from price;
 * this one argues from hardware, which is the actual position: Pakistan, like
 * every market with Nvidia import constraints, is going to run on NPUs, and
 * the gateway is built for that silicon rather than ported onto it. The copy
 * is the team's, lightly punctuated.
 *
 * Layout, since the same evening: the argument on the left, a die of NPU
 * cores on the right that stays pinned while the text scrolls past it and
 * lets go when the text ends (a sticky cell spanning the text column's rows).
 * The die is live: activity sweeps across it, the cursor lights and lifts the
 * cores under it, a click drops a ring. On phones it sits between the heading
 * and the argument at a fixed height, not pinned.
 *
 * The reading effect: every word starts at a fifth of the ink and comes up to
 * full foreground as the block scrolls through the middle of the viewport, a
 * few words at a time. Full foreground is dark on the light theme and
 * near-white on the dark one. Under `prefers-reduced-motion` every word
 * renders at full ink and the die holds still.
 *
 * The section closes on a full-width band, "CUDA to CANN translation", added
 * 2026-08-30; see `TranslationBand` for what it argues and how it moves.
 *
 * The section is shared with the PK Endpoint page (`numbered={false}` there,
 * because that page has no "2." and "3." for the number to lead into).
 */

const PARAGRAPHS: string[] = [
  "There is a learning-curve challenge to using Huawei's Ascend NPUs.",
  "But Pakistan, along with other countries that face Nvidia import challenges, will mostly have NPUs in the country.",
  "We are here to bridge that gap. We give you highly optimised open-source model endpoints, with a sovereign gateway that works on both Huawei NPUs and Nvidia GPUs.",
  "Dedicated support, and accountability for uptime, on Huawei NPUs.",
];

const FACTS = [
  {
    label: "Huawei Ascend 910B",
    body: "64GB NPUs, racked in country rather than rented by the hour in a foreign region.",
  },
  {
    label: "One gateway, two kinds of silicon",
    body: "The same route id serves from Ascend NPUs or Nvidia GPUs. Your code does not know which, and does not need to.",
  },
  {
    label: "Accountable uptime",
    body: "The people who wrote the Ascend serving stack are the people on call for it. Dedicated support, not a ticket queue.",
  },
];

const WORDS = PARAGRAPHS.map((p) => p.split(" "));
const TOTAL = WORDS.reduce((n, w) => n + w.length, 0);

function Word({
  text,
  index,
  progress,
  reduced,
}: {
  text: string;
  index: number;
  progress: MotionValue<number>;
  reduced: boolean;
}) {
  // Each word owns a short window of the scroll range. Windows overlap so a
  // handful of words are mid-fade at any moment: a sweep, not a typewriter.
  const start = index / TOTAL;
  const end = Math.min(1, start + 4 / TOTAL);
  /**
   * The floor is 0.6, raised from 0.18 on 2026-09-10.
   *
   * At 0.18 the foreground over the page background measures 1.47 against the
   * 4.5 WCAG AA needs, and PageSpeed failed roughly sixty words of this
   * paragraph on contrast. That was not an audit technicality: this is the
   * section's actual copy, and before the reader scrolls into it, it was
   * genuinely close to unreadable.
   *
   * 0.6 is the lowest value that clears 4.5 on both themes (4.70 light, 6.17
   * dark). The sweep still reads as a sweep, with less range to travel.
   * **Anything below 0.6 fails**, so do not lower it for effect.
   */
  const opacity = useTransform(progress, [start, end], [0.6, 1]);
  return (
    <motion.span style={reduced ? undefined : { opacity }} className="inline-block">
      {text}
    </motion.span>
  );
}

/**
 * One end of the rail: a stack name, set as the code it is.
 *
 * Bare type, no pill. It wore a rounded outline for the length of one draft
 * and read as two buttons rather than two labels on a wire, which is the
 * opposite of what the rail is saying.
 */
const RAIL_LABEL =
  "shrink-0 font-mono text-[0.68rem] uppercase tracking-[0.16em] sm:text-[0.72rem]";

/**
 * CUDA to CANN translation: the band that closes the section.
 *
 * Added 2026-08-30, the team's copy. The argument above it is that the silicon
 * in this market is going to be Ascend; this answers the question that always
 * follows, which is what happens to the CUDA model you have already built. It
 * runs the full width under both columns rather than sitting in the left-hand
 * list, because it is the conclusion of the argument and not another fact
 * beside it.
 *
 * The motion is the section's own vocabulary rather than a new one: the die's
 * activity sweep, flattened onto a hairline. A pulse crosses the rail from the
 * CUDA chip to the CANN chip, and the CANN chip lights as it lands, which is
 * the whole claim in one gesture. Under `prefers-reduced-motion` the rail is a
 * plain rule, nothing travels and nothing lights.
 */
const RAIL_S = 2.8;
const RAIL_GAP = 0.7;

function TranslationBand({ reduced }: { reduced: boolean }) {
  return (
    <Reveal as="div" className="mt-16 border-t border-border pt-14 sm:mt-24 sm:pt-20" amount={0.15}>
      <div className="mx-auto max-w-3xl text-center">
        <p className="eyebrow">Already built on CUDA</p>

        <div className="mx-auto mt-7 flex w-full max-w-xs items-center gap-3 sm:mt-9 sm:max-w-sm sm:gap-4">
          <span className={`${RAIL_LABEL} text-muted`}>CUDA</span>
          <div className="relative h-px flex-1 bg-border">
            {!reduced && (
              <motion.span
                aria-hidden="true"
                className="absolute top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-foreground"
                initial={{ left: "0%", opacity: 0 }}
                animate={{ left: ["0%", "6%", "94%", "100%"], opacity: [0, 1, 1, 0] }}
                transition={{
                  duration: RAIL_S,
                  times: [0, 0.12, 0.86, 1],
                  ease: "easeInOut",
                  repeat: Infinity,
                  repeatDelay: RAIL_GAP,
                }}
              />
            )}
          </div>
          {/* The destination lights as the pulse lands. Ink, not a plate: the
              span is foreground-coloured and its opacity does the work, so
              there is nothing to draw a box around. */}
          <motion.span
            className={`${RAIL_LABEL} text-foreground`}
            /* Resting opacity 0.6, raised from 0.45 on 2026-09-10 for the
               same reason as the word reveal above: foreground at 0.45 over
               the page background measures 2.94 against the 4.5 AA needs, and
               this label sits at its resting value most of the time. Lighthouse
               happened to sample it lit, so it passed the audit while still
               being hard to read. 0.6 is the lowest value that clears 4.5 on
               both themes, and the pulse to 1 still reads. */
            initial={{ opacity: reduced ? 1 : 0.6 }}
            animate={reduced ? { opacity: 1 } : { opacity: [0.6, 0.6, 1, 0.6] }}
            transition={
              reduced
                ? undefined
                : {
                    duration: RAIL_S,
                    times: [0, 0.78, 0.9, 1],
                    ease: "easeOut",
                    repeat: Infinity,
                    repeatDelay: RAIL_GAP,
                  }
            }
          >
            CANN
          </motion.span>
        </div>

        <h3 className="mt-8 font-serif text-[clamp(1.9rem,4.6vw,3.4rem)] font-light leading-[1.08] tracking-[-0.015em] text-foreground sm:mt-10">
          CUDA to CANN translation.
        </h3>
        <p className="mx-auto mt-5 max-w-2xl font-sans text-[0.95rem] leading-relaxed text-muted sm:mt-6 sm:text-base">
          If your model is CUDA-native and already prepared for deployment on GPUs, that is not a
          blocker. We translate the model code to run on CANN, Huawei&rsquo;s Ascend compute stack,
          and serve your inference on NPUs with the performance numbers to show for it.
        </p>
      </div>
    </Reveal>
  );
}

export function NpuNative({ numbered = true }: { numbered?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion() ?? false;
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 0.85", "end 0.42"],
  });

  let running = 0;

  return (
    <section id="problem" className="relative scroll-mt-24 overflow-x-clip">
      <div className="mx-auto max-w-[90rem] px-5 py-16 sm:px-8 sm:py-32">
        <div className="grid gap-x-12 gap-y-10 lg:grid-cols-2 lg:gap-x-16 xl:grid-cols-[minmax(0,11fr)_minmax(0,9fr)]">
          {/* heading: first on every viewport */}
          <div className="order-1 lg:col-start-1">
            <SectionHeading
              align="left"
              eyebrow={numbered ? "1. NPU-native inference in Pakistan" : "NPU-native inference in Pakistan"}
              title={<>Huawei NPUs are going to get better and be everywhere.</>}
            />
          </div>

          {/* the die: second on phones, a pinned right column on desktop */}
          <div className="order-2 lg:col-start-2 lg:row-span-2 lg:row-start-1">
            <div className="lg:sticky lg:top-24">
              <div className="relative h-[64vw] max-h-[24rem] w-full sm:h-[26rem] sm:max-h-none lg:h-[calc(100vh-8rem)] lg:max-h-[46rem]">
                <div className="absolute inset-0">
                  <DieCanvas />
                </div>
                <p
                  aria-hidden="true"
                  className="pointer-events-none absolute bottom-0 left-0 font-sans text-[0.68rem] uppercase tracking-[0.14em] text-muted-2"
                >
                  Ascend 910B, one die of cores. Move across it; click to schedule a job.
                </p>
              </div>
            </div>
          </div>

          {/* the argument: last on phones, under the heading on desktop */}
          <div className="order-3 lg:col-start-1">
            <div ref={ref} className="mt-2 sm:mt-4">
              {PARAGRAPHS.map((p, pi) => {
                const words = WORDS[pi];
                const base = running;
                running += words.length;
                return (
                  <p
                    key={pi}
                    className="mb-6 max-w-2xl font-serif text-[clamp(1.4rem,2.6vw,2.2rem)] font-light leading-[1.3] tracking-[-0.01em] text-foreground last:mb-0"
                  >
                    {words.map((w, wi) => (
                      <span key={wi}>
                        <Word text={w} index={base + wi} progress={scrollYProgress} reduced={reduced} />
                        {wi < words.length - 1 ? " " : ""}
                      </span>
                    ))}
                  </p>
                );
              })}
            </div>

            <Reveal as="div" className="mt-14 max-w-2xl sm:mt-20" amount={0.2}>
              <dl className="divide-y divide-border border-y border-border">
                {FACTS.map((f) => (
                  <div key={f.label} className="grid gap-2 py-5 sm:grid-cols-[13rem_1fr] sm:gap-6">
                    <dt className="font-serif text-lg leading-snug text-foreground">{f.label}</dt>
                    <dd className="font-sans text-sm leading-relaxed text-muted">{f.body}</dd>
                  </div>
                ))}
              </dl>
            </Reveal>
          </div>
        </div>

        {/* Full width, under both columns: the conclusion of the argument. */}
        <TranslationBand reduced={reduced} />
      </div>
    </section>
  );
}
